import {FormControl, Select} from '@primer/react-brand/esm'
import {t, getLocale} from '../../../../../../lib/utils/localization'
import {getCountryName, sortByLocalizedName} from '../../../../../../lib/utils/country-names'
import * as z from 'zod'
import {useCallback, use, useMemo} from 'react'
import {FormContext} from '../../../FormContext'
import {getAnalyticsEvent} from '../../../../../../lib/utils/analytics'
import {ConsentExperienceContext} from '../ConsentExperienceContext'
import countriesEs from './countries/es'
import countriesKo from './countries/ko'
import countriesPt from './countries/pt'
import countriesJa from './countries/ja'

const validations = [
  {
    schema: z.string().min(1),
    message: t('Please select your country/region'),
  },
]

export const CountrySelectField = () => {
  const validationErrorId = 'country-validation-msg'
  const formContext = use(FormContext)
  const countryError = formContext?.errors['country']
  const {selectedCountry, setSelectedCountry} = use(ConsentExperienceContext)
  const ctx = use(ConsentExperienceContext)
  const locale = getLocale()
  const countries = useMemo(() => {
    const countryList = (() => {
      switch (locale) {
        case 'es':
          return countriesEs
        case 'ko':
          return countriesKo
        case 'pt':
          return countriesPt
        case 'ja':
          return countriesJa
        default:
          return ctx.marketingTargetedCountries || []
      }
    })()

    return sortByLocalizedName(countryList, c => getCountryName(c.alpha))
  }, [ctx.marketingTargetedCountries, locale])
  const registerProps = formContext?.register('country', {
    label: t('Country'),
    required: true,
    validations,
  })
  const registeredOnChange = registerProps?.onChange

  const handleCountryChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      registeredOnChange?.(e)
      const selectedAlpha = e.target.value
      const country = countries.find(c => c.alpha === selectedAlpha) || null
      setSelectedCountry(country)
    },
    [countries, registeredOnChange, setSelectedCountry],
  )

  return (
    <FormControl
      id="form-field-country"
      validationStatus={typeof countryError === 'string' ? 'error' : undefined}
      fullWidth
      required
    >
      <FormControl.Label>{t('Country')}</FormControl.Label>

      <Select
        {...registerProps}
        {...getAnalyticsEvent({
          action: 'country',
          tag: 'select',
          context: 'form_control',
          location: 'form',
        })}
        aria-describedby={validationErrorId}
        onChange={handleCountryChange}
        value={selectedCountry?.alpha || ''}
      >
        <Select.Option value="">{t('Choose your country/region')}</Select.Option>

        {countries.map(country => (
          <Select.Option key={country.alpha} value={country.alpha}>
            {getCountryName(country.alpha)}
          </Select.Option>
        ))}
      </Select>

      {typeof countryError === 'string' ? (
        <FormControl.Validation id={validationErrorId}>{countryError}</FormControl.Validation>
      ) : null}
    </FormControl>
  )
}
